import { useDispatch, useSelector } from "react-redux";
import { getErrorMessage } from "../../../utils/errorUtils";
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty } from "../../../utils/objectUtils";
import logger from "../../../utils/logger";
import { useOrchestrator } from "../orchestrator";
import { useCallback, useMemo } from "react";
import { fromUtcToDeviceTimeZoneDateOnly } from "../../../utils/dateFormatter";
import { toName } from "../../../utils/stringFormatter";
import Encounter from "../../../models/Encounter";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { toast } from "react-toastify";
import NewMessageToast from "../../../components/NewMessageToast";
import { orderBy } from "lodash";
import moment from "moment-timezone";
import { v4 as uuid } from "uuid";

const initialState = {
	encounters: [],
	activeEncounterId: null,
	// a message the patient has sent but the server has not confirmed. It lives here rather than
	// on the encounter because the snapshot listener replaces `encounters` wholesale, which would
	// wipe an optimistic entry mid-flight.
	pendingMessagesByEncounterId: {}
};

/**
 * Appends the messages still awaiting confirmation to the ones the server has.
 *
 * A queued message shares its clientId with the stored one, so the confirmed copy wins the moment
 * it lands rather than the patient seeing the same message twice. Returns the confirmed array
 * itself when nothing is pending, because the thread's scroll effect keys on this identity.
 *
 * @param {Array<object>} confirmedMessages - Messages stored on the encounter.
 * @param {Array<object>} pendingMessages - Queued messages for that encounter.
 * @returns {Array<object>}
 */
export const mergePendingMessages = (confirmedMessages = [], pendingMessages = []) => {
	if (isListEmpty(pendingMessages)) return confirmedMessages;

	const unconfirmed = pendingMessages.filter((item) => (
		!confirmedMessages.some((message) => message?.id === item?.clientId)
	));
	if (isListEmpty(unconfirmed)) return confirmedMessages;

	return [...confirmedMessages, ...unconfirmed];
};

const withPendingMessage = (state, encounterId, updateList) => ({
	...state,
	pendingMessagesByEncounterId: {
		...state.pendingMessagesByEncounterId,
		[encounterId]: updateList(state.pendingMessagesByEncounterId[encounterId] ?? [])
	}
});

// one listener per client. getProfile attaches this, and getProfile runs again on every claim
// change and on every impersonation, so without a handle each of those left a live snapshot behind.
let encountersUnsubscribe = null;
let encountersListenerUserId = null;

// this rule is `isUser(resource.data.userId)` with no `exists(/users/{uid})` clause, so a users
// document that has not committed yet cannot deny it - only a request carrying no usable token can.
// that makes a retry here diagnostic: if this one ever fires, the rejection the notifications
// listener sees is token propagation rather than an uncommitted write.
const LISTEN_RETRY_DELAYS_MS = [1000, 3000, 8000];

let encountersRetryCount = 0;
let encountersRetryUserId = null;
let encountersRetryTimeoutId = null;

const stopEncountersListener = () => {
	if (isNotNullOrUndefined(encountersUnsubscribe)) {
		encountersUnsubscribe();
	}

	encountersUnsubscribe = null;
	encountersListenerUserId = null;
};

const clearPendingEncountersRetry = () => {
	if (isNotNullOrUndefined(encountersRetryTimeoutId)) {
		clearTimeout(encountersRetryTimeoutId);
	}

	encountersRetryTimeoutId = null;
};

const cancelEncountersRetry = () => {
	clearPendingEncountersRetry();

	encountersRetryCount = 0;
	encountersRetryUserId = null;
};

const chatReducer = (state = initialState, action) => {
	const { type, payload } = action;
	switch (type) {
		case 'SEND_MESSAGE':
			return {
				...state,
				encounters: state.encounters.map((encounter) => {
					if (encounter.id === payload.encounterId) {
						let existingMessages = isListNotEmpty(encounter.messages) ? [...encounter.messages] : [];
						let updatedMessages = existingMessages;
						// if the message doesn't already exist on the encounter object, add it
						if (!existingMessages.some(i => i.id === payload?.newMessage?.id)) {
							updatedMessages = [...existingMessages, payload.newMessage];
						}

						return {
							...encounter,
							messages: updatedMessages,
							messagesCount: updatedMessages.length,
							lastMessageAt: payload?.newMessage?.sentAt,
							lastMessageBy: payload?.newMessage?.sentBy,
							isRead: true,
							unreadCount: 0
						};
					} else {
						return encounter;
					}
				})
			};
		case 'CHANGE_ENCOUNTER':
			return {
				...state,
				activeEncounterId: payload.encounterId,
				encounters: state.encounters.map((encounter) =>
					encounter.id === payload.encounterId
						? {
							...encounter,
							isRead: true,
							unreadCount: 0
						}
						: encounter
				)
			};
		case 'UPDATE_ENCOUNTER':
			return {
				...state,
				encounters: state.encounters.map((encounter) =>
					encounter.id === payload.id ? { ...payload } : encounter
				)
			};
		case 'QUEUE_MESSAGE':
			return withPendingMessage(state, payload.encounterId, (pending) => [
				...pending.filter((item) => item.clientId !== payload.clientId),
				{ ...payload, status: 'PENDING' }
			]);
		case 'RESOLVE_MESSAGE':
		case 'DISCARD_MESSAGE':
			return withPendingMessage(state, payload.encounterId, (pending) => (
				pending.filter((item) => item.clientId !== payload.clientId)
			));
		case 'FAIL_MESSAGE':
			// stays in place so the patient keeps their text and the bubble does not jump
			return withPendingMessage(state, payload.encounterId, (pending) => (
				pending.map((item) => (
					item.clientId === payload.clientId
						? { ...item, status: 'FAILED' }
						: item
				))
			));
		case 'LOAD_ENCOUNTERS':
			// deliberately leaves pendingMessagesByEncounterId alone: a snapshot landing while a
			// send is in flight must not take the optimistic bubble with it
			return {
				...state,
				encounters: payload
			};
		case 'RESET_ENCOUNTERS':
			return {
				...state,
				...initialState
			};
		default:
			return state;
	}
};

export const loadEncounters = (list) => {
	return async (dispatch) => {
		try {
			dispatch({ type: 'LOAD_ENCOUNTERS', payload: list });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const changeActiveEncounter = (encounterId) => {
	return async (dispatch, getState) => {
		try {
			if (!encounterId) {
				dispatch({ type: 'CHANGE_ENCOUNTER', payload: { encounterId: null } });
				return;
			}
			const state = getState();
			const chatState = { ...state.chatState };
			const { encounters } = chatState;

			let existingEncounter = encounters?.find((e) => e.id === encounterId);
			if (isObjectNotEmpty(existingEncounter)) {
				dispatch({ type: 'CHANGE_ENCOUNTER', payload: { encounterId: encounterId } });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

/**
 * Sends a message, showing it in the thread before the server confirms it.
 *
 * The queued entry is what the patient sees immediately; it is removed once the write comes
 * back, or marked FAILED so the message stays on screen with their text intact. Most failures
 * here are not exceptions — Encounter.sendMessage answers undefined for a non-200 from the
 * pharmacy and for a failed write — so the absence of a message is what decides failure.
 *
 * @param {string} encounterId - Encounter the message belongs to.
 * @param {string|null} message - Message body.
 * @param {string|null} [mediaRefId] - Attached image reference.
 * @param {object} profile - Sender profile.
 * @param {boolean} [testMode] - Whether the encounter is a test encounter.
 * @param {string} [clientId] - Id shared by the optimistic entry and the stored message.
 * @returns {Promise<boolean>}
 */
export const sendMessage = (encounterId, message, mediaRefId = null, profile, testMode = false, clientId = uuid()) => {
	return async (dispatch) => {
		dispatch({
			type: 'QUEUE_MESSAGE',
			payload: {
				clientId,
				encounterId,
				message,
				mediaRefId,
				sentName: toName(profile),
				sentBy: profile?.userId ?? null,
				sentAt: moment().utc().toDate(),
			}
		});

		try {
			const newMessage = await Encounter.sendMessage(encounterId, message, mediaRefId, profile, testMode, clientId);
			if (isObjectNotEmpty(newMessage)) {
				dispatch({
					type: 'SEND_MESSAGE',
					payload: {
						encounterId: encounterId,
						newMessage: newMessage
					}
				});
				dispatch({ type: 'RESOLVE_MESSAGE', payload: { encounterId, clientId } });

				return true;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		dispatch({ type: 'FAIL_MESSAGE', payload: { encounterId, clientId } });

		return false;
	}
};

/**
 * Drops a failed message the patient has chosen not to keep.
 *
 * @param {string} encounterId - Encounter the message belongs to.
 * @param {string} clientId - Id of the queued message.
 * @returns {Function}
 */
export const discardPendingMessage = (encounterId, clientId) => {
	return (dispatch) => {
		dispatch({ type: 'DISCARD_MESSAGE', payload: { encounterId, clientId } });
	};
};

export const markEncounterAsRead = (encounterId) => {
	return async (dispatch) => {
		try {
			const encounter = await Encounter.getById(encounterId);
			if (isObjectNotEmpty(encounter)) {

				let existingMessages = isListNotEmpty(encounter.messages) ? [...encounter.messages] : [];
				let updatedMessages = existingMessages.map((message) => {
					if (isBooleanFalse(message.isRead)) {
						return {
							...message,
							isRead: true,
							readAt: moment().utc().toDate()
						};
					} else {
						return message;
					}
				});

				return await Encounter.updateFields(encounterId, { isRead: true, unreadCount: 0, messages: [...updatedMessages] });
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}

		return false;
	}
};

export const resetChat = () => {
	return async (dispatch) => {
		try {
			cancelEncountersRetry();
			stopEncountersListener();

			dispatch({ type: 'RESET_ENCOUNTERS', payload: null });
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

// Loads encounters and listens for changes.
export const refreshEncounters = (userId) => {
	return async (dispatch, getState) => {
		try {
			if (isStringNotEmpty(userId)) {
				// same guard as the notifications listener, for the same reason: this runs on whoever is
				// signed in now, not on whoever it was dispatched for. the encounters rule refuses a
				// cross-account listen, but an accepted one would still replace the current customer's.
				if (auth.currentUser?.uid !== userId) {
					return;
				}

				if (userId === encountersListenerUserId) {
					return;
				}

				clearPendingEncountersRetry();
				stopEncountersListener();

				// the budget belongs to one listener, so only a different user starts it over - resetting
				// it here unconditionally would make the retry re-entry an unbounded loop.
				if (userId !== encountersRetryUserId) {
					encountersRetryUserId = userId;
					encountersRetryCount = 0;
				}

				// Create the query to load the encounters and listen for new ones.
				const encountersQuery = query(collection(db, 'encounters'), where('userId', '==', userId));

				// Start listening to the query.
				encountersUnsubscribe = onSnapshot(encountersQuery, function (snapshot) {
					// console.log('encounters updated');

					snapshot.docChanges({ includeMetadataChanges: false }).forEach((change) => {
						if (change.type === "modified") {
							let updatedEncounter = { id: change.doc.id, ...change.doc.data() };
							// console.log("Updated encounter: ", updatedEncounter);
							let newMessages = updatedEncounter.messages.filter(i => i.sentBy && userId && i.sentBy !== userId && i.isRead === false);
							if (isListNotEmpty(newMessages)) {
								let latestChatMessage = newMessages[newMessages.length - 1];

								const state = getState();
								const orchestrator = { ...state.orchestrator };
								const { providers } = orchestrator;

								toast(<NewMessageToast chatMessage={latestChatMessage} changeActiveEncounter={(encounterId) => { dispatch(changeActiveEncounter(encounterId)); }} encounter={updatedEncounter} getProviderDetails={(providerId) => { return providers.find((provider) => provider.userId === providerId); }} />, {
									toastId: latestChatMessage?.id ?? `new-message-${uuid()}`
								});
							}
						}
					});

					let list = snapshot.docs.map((doc) => {
						return {
							id: doc.id, // append document id to each document
							...doc.data()
						};
					});

					// console.log('encounters', list);
					let orderedList = orderBy(list, ['lastMessageAt'], ['desc']);
					// console.log('ordered encounters', orderedList);
					encountersRetryCount = 0;

					dispatch(loadEncounters(orderedList));

				}, (error) => {
					// a rejected listen is terminal - firestore never retries it - so the handle has to be
					// dropped or a dead listener stays recorded as attached and nothing re-attaches.
					stopEncountersListener();

					const hasRetryRemaining = encountersRetryCount < LISTEN_RETRY_DELAYS_MS.length;
					logger.logErrorEvent('REFRESH_ENCOUNTERS_FAILED', error, `encounters listener: ${getErrorMessage(error)} (attempt ${encountersRetryCount + 1}${hasRetryRemaining ? '' : ', giving up'})`, true);

					if (hasRetryRemaining) {
						const retryDelayMs = LISTEN_RETRY_DELAYS_MS[encountersRetryCount];
						encountersRetryCount = encountersRetryCount + 1;

						encountersRetryTimeoutId = setTimeout(() => {
							encountersRetryTimeoutId = null;
							dispatch(refreshEncounters(userId));
						}, retryDelayMs);
					}
				});

				encountersListenerUserId = userId;
			}
		} catch (error) {
			logger.logReduxErrorEvent(error, getErrorMessage(error), true);
		}
	}
};

export const useChat = () => {
	const { providers } = useOrchestrator();

	const dispatch = useDispatch();

	const encounters = useSelector((appState) => appState.chatState.encounters);
	const activeEncounterId = useSelector((appState) => appState.chatState.activeEncounterId);

	// get provider details from given providerId
	const getProviderDetails = useCallback((providerId) => {
		return providers.find((provider) => provider.userId === providerId);
	}, [providers]);

	// get encounter by passing encounter id
	const getEncounter = useCallback((encounterId) => {
		return encounters.find((encounter) => encounter.id === encounterId) ?? null;
	}, [encounters]);

	// get all messages by passing encounter id
	const getEncounterMessages = useCallback((encounterId) => {
		let result = encounters.find((encounter) => encounter.id === encounterId);
		return typeof result === 'object' && result.messages.length > 0
			? result.messages
			: [];
	}, [encounters]);

	// get last message from given encounter
	const getLastMessage = useCallback((encounter) => {
		let result = encounters.find((e) => e.id === encounter?.id);
		return typeof result === 'object' && result.messages.length > 0
			? (result.messages[result.messages.length - 1].mediaRefId ? '[Image]' : result.messages[result.messages.length - 1].message)
			: (isBooleanTrue(encounter.isStarted) ? "Let's start chating" : "<i class='fa fa-calendar-day' /> " + fromUtcToDeviceTimeZoneDateOnly(encounter.scheduledAt));
	}, [encounters]);

	// get last message from given encounter
	const getLastMessageDateTime = useCallback((encounter) => {
		let result = encounters.find((e) => e.id === encounter?.id);
		if (typeof result === 'object' && result.messages.length > 0) {
			return fromUtcToDeviceTimeZoneDateOnly(result?.lastMessageAt, 'MMM D');
		} else {
			return '';
		}
	}, [encounters]);

	const activeEncounter = useMemo(() => {
		if (isStringNotEmpty(activeEncounterId)) {
			return encounters.find((encounter) => encounter.id === activeEncounterId) ?? null;
		}

		return null;
	}, [activeEncounterId, encounters]);

	const activeEncounterOtherUser = useMemo(() => {
		if (isListNotEmpty(providers) && isStringNotEmpty(activeEncounter?.providerId)) {
			return providers?.find((provider) => provider.userId === activeEncounter?.providerId) ?? null;
		}

		return null;
	}, [activeEncounter?.providerId, providers]);

	const pendingMessagesByEncounterId = useSelector((appState) => appState.chatState.pendingMessagesByEncounterId);

	const activeEncounterMessages = useMemo(() => {
		let confirmedMessages = [];
		if (isListNotEmpty(encounters) && isStringNotEmpty(activeEncounterId)) {
			let result = encounters.find((encounter) => encounter.id === activeEncounterId);
			confirmedMessages = isListNotEmpty(result?.messages) ? result.messages : [];
		}

		const pending = isStringNotEmpty(activeEncounterId)
			? (pendingMessagesByEncounterId?.[activeEncounterId] ?? [])
			: [];

		return mergePendingMessages(confirmedMessages, pending);
	}, [encounters, activeEncounterId, pendingMessagesByEncounterId]);

	const unreadEncounterMessageCount = useMemo(() => {
		if (isListNotEmpty(encounters)) {
			return encounters.reduce((total, encounter) => total + encounter.unreadCount, 0);
		}

		return 0;
	}, [encounters]);

	return {
		ChatState: useSelector((appState) => appState.chatState),

		encounters: useSelector((appState) => appState.chatState.encounters),
		activeEncounterId: useSelector((appState) => appState.chatState.activeEncounterId),
		activeEncounter: activeEncounter,
		activeEncounterOtherUser: activeEncounterOtherUser,
		activeEncounterMessages: activeEncounterMessages,
		pendingMessagesByEncounterId: pendingMessagesByEncounterId,
		unreadEncounterMessageCount: unreadEncounterMessageCount,

		getProviderDetails: getProviderDetails,
		getEncounter: getEncounter,
		getEncounterMessages: getEncounterMessages,
		getLastMessage: getLastMessage,
		getLastMessageDateTime: getLastMessageDateTime,
		markEncounterAsRead: (encounterId) => dispatch(markEncounterAsRead(encounterId)),
		refreshEncounters: (userId) => dispatch(refreshEncounters(userId)),

		loadEncounters: (list) => dispatch(loadEncounters(list)),
		changeActiveEncounter: (encounterId) => dispatch(changeActiveEncounter(encounterId)),
		sendMessage: (encounterId, message, mediaRefId = null, profile, testMode = false, clientId = undefined) => dispatch(sendMessage(encounterId, message, mediaRefId, profile, testMode, clientId)),
		discardPendingMessage: (encounterId, clientId) => dispatch(discardPendingMessage(encounterId, clientId)),
		resetChat: () => dispatch(resetChat())
	};
};

export default chatReducer;